import "./public-booking-flow.scss";

import { AvailableSlotDto, MessageResponseDto } from "@api/api";
import { registrationFormDefault } from "@factory/form/form.default";
import {
  RegistrationFormRule,
  RegistrationFormSchema,
} from "@factory/form/form.rule";
import { useQyGetPublicWeeklySchedule } from "@factory/query/clinic-schedule.query";
import {
  useQyCheckBookingLimit,
  useQyCreateRegistration,
} from "@factory/query/registration.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { LabelValue } from "@shared/models/label-value.interface";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import {
  getBookingLimitMessage,
  isBookingLimitAllowed,
} from "@shared/utility/public-booking";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useCallback, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { appConfig } from "../../../app.config";
import BookingCalendar from "../booking-calendar/booking-calendar";
import BookingSummary from "../booking-summary/booking-summary";
import FormRegistration from "../form-registration/form-registration";
import TimeSlotSelection from "../time-slot-selection/time-slot-selection";

type BookingStep = "calendar" | "time" | "details";

interface PublicBookingFlowProps {
  serviceOptions?: LabelValue[];
  isFetchingServices?: boolean;
  contactInfo: {
    email: string;
    phone: string;
    postRegistrationLink?: string;
  };
  clinicName?: string;
  tenant: string;
}

const STEPS = [
  { key: "calendar", label: "Select Date", number: 1 },
  { key: "time", label: "Select Time", number: 2 },
  { key: "details", label: "Your Details", number: 3 },
] as const;

export function PublicBookingFlow({
  serviceOptions,
  isFetchingServices,
  contactInfo,
  clinicName,
  tenant,
}: PublicBookingFlowProps) {
  const { showError } = useNotificationContext();
  const { mapCreateRegistration } = useFormApiMapper();

  // Booking flow state
  const [currentStep, setCurrentStep] = useState<BookingStep>("calendar");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlotDto | null>(
    null,
  );
  const [selectedService, setSelectedService] = useState<string>("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bookingReference, setBookingReference] = useState<string>("");
  const [showBookingLimitModal, setShowBookingLimitModal] = useState(false);
  const [bookingLimitMessage, setBookingLimitMessage] = useState<string>("");

  // Form setup
  const formMethod = useForm<RegistrationFormSchema>({
    defaultValues: registrationFormDefault,
    resolver: zodResolver(RegistrationFormRule),
  });

  // Logo
  const logo = useMemo(
    () =>
      `${appConfig.bucketAssetPath}/${tenant}/icon-192x192.png?${new Date().toISOString()}`,
    [tenant],
  );

  // Service name lookup
  const serviceName = useMemo(() => {
    if (!selectedService || !serviceOptions) return undefined;
    const found = serviceOptions.find((s) => s.value === selectedService);
    return found?.label;
  }, [selectedService, serviceOptions]);

  // Handle API success
  const handleApiSuccess = useCallback((response: MessageResponseDto) => {
    const data = response.data as { code?: string } | undefined;
    setBookingReference(data?.code || "");
    setShowSuccessModal(true);
  }, []);

  const { mutate: createRegistration, isLoading: isCreating } =
    useQyCreateRegistration(handleApiSuccess);
  const { mutateAsync: checkBookingLimit } = useQyCheckBookingLimit();

  // Weekly schedule — used to determine if time selection should be skipped
  const { data: weeklySchedule } = useQyGetPublicWeeklySchedule(tenant);
  const skipTimeSelection = !!(weeklySchedule ?? []).some(
    (s) =>
      s.enable_public_booking_time_selection === true ||
      (s.enable_public_booking_time_selection as unknown) === 1,
  );
  const showServicesFromSchedule = !!(weeklySchedule ?? []).some(
    (s) =>
      (s as { show_services_in_public_booking?: boolean | number })
        .show_services_in_public_booking === true ||
      (s as { show_services_in_public_booking?: boolean | number })
        .show_services_in_public_booking === 1,
  );
  const visibleSteps = skipTimeSelection
    ? [STEPS[0], { ...STEPS[2], number: 2 }]
    : STEPS;

  // Navigation handlers
  const handleDateSelect = useCallback(
    async (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      try {
        const response = await checkBookingLimit({
          tenant,
          date: formattedDate,
        });
        if (!isBookingLimitAllowed(response)) {
          setBookingLimitMessage(
            getBookingLimitMessage(response) ||
              "Booking limit reached for the selected date.",
          );
          setShowBookingLimitModal(true);
          return;
        }
      } catch {
        return;
      }

      setSelectedDate(date);
      if (skipTimeSelection) {
        setSelectedSlot({
          start_time: "",
          end_time: "",
          available_spots: 1,
          is_available: true,
        });
        setCurrentStep("details");
      } else {
        setSelectedSlot(null);
        setCurrentStep("time");
      }
    },
    [skipTimeSelection, checkBookingLimit, tenant],
  );

  const handleSlotSelect = useCallback((slot: AvailableSlotDto) => {
    setSelectedSlot(slot);
  }, []);

  const handleContinueToDetails = useCallback(() => {
    if (selectedSlot) {
      setCurrentStep("details");
    }
  }, [selectedSlot]);

  const handleBackToCalendar = useCallback(() => {
    setCurrentStep("calendar");
  }, []);

  const handleBackToTimeSelection = useCallback(() => {
    setCurrentStep(skipTimeSelection ? "calendar" : "time");
  }, [skipTimeSelection]);

  const handleChangeDate = useCallback(() => {
    setCurrentStep("calendar");
  }, []);

  const handleChangeTime = useCallback(() => {
    setCurrentStep(skipTimeSelection ? "calendar" : "time");
  }, [skipTimeSelection]);

  // Form submission
  const handleSubmit = useCallback(() => {
    formMethod.handleSubmit(
      (form: RegistrationFormSchema) => {
        if (!selectedDate || !selectedSlot || !tenant) {
          showError("Please complete all booking steps");
          return;
        }

        // Format date in local timezone to avoid UTC conversion issues
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
        const day = String(selectedDate.getDate()).padStart(2, "0");
        const formattedDate = `${year}-${month}-${day}`;

        const updatedForm = {
          ...form,
          tenant: tenant,
          service: selectedService || form.service,
          scheduleDate: formattedDate,
          scheduleTime: skipTimeSelection ? "" : selectedSlot.start_time,
        };

        const formData = mapCreateRegistration(updatedForm);
        createRegistration(formData);
      },
      () => {
        showError("Please fill in all required fields");
      },
    )();
  }, [
    formMethod,
    selectedDate,
    selectedSlot,
    selectedService,
    tenant,
    mapCreateRegistration,
    createRegistration,
    showError,
  ]);

  const handleCloseSuccess = useCallback(() => {
    setShowSuccessModal(false);
    // Reset the form
    setCurrentStep("calendar");
    setSelectedDate(null);
    setSelectedSlot(null);
    formMethod.reset(registrationFormDefault);
  }, [formMethod]);

  // Step status helpers
  const getStepStatus = useCallback(
    (stepKey: string): "completed" | "active" | "pending" => {
      const stepOrder = skipTimeSelection
        ? ["calendar", "details"]
        : ["calendar", "time", "details"];
      const currentIndex = stepOrder.indexOf(currentStep);
      const stepIndex = stepOrder.indexOf(stepKey);

      if (stepIndex < currentIndex) return "completed";
      if (stepIndex === currentIndex) return "active";
      return "pending";
    },
    [currentStep, skipTimeSelection],
  );

  if (!tenant) {
    return <div>Invalid clinic</div>;
  }

  return (
    <div className="public-booking-flow">
      <div className="booking-container">
        <div className="booking-header">
          <img src={logo} alt="Clinic Logo" className="clinic-logo" />
          <h1>Book Your Appointment</h1>
          <p>Schedule your visit in just a few steps</p>
        </div>

        <div className="booking-steps">
          {visibleSteps.map((step, index) => (
            <>
              <div key={step.key} className={`step ${getStepStatus(step.key)}`}>
                <span className="step-number">
                  {getStepStatus(step.key) === "completed" ? (
                    <i className="pi pi-check"></i>
                  ) : (
                    step.number
                  )}
                </span>
                <span className="step-label">{step.label}</span>
              </div>
              {index < visibleSteps.length - 1 && (
                <div
                  className={`step-connector ${
                    getStepStatus(visibleSteps[index + 1].key) !== "pending"
                      ? "completed"
                      : ""
                  }`}
                ></div>
              )}
            </>
          ))}
        </div>

        <div className="booking-content">
          {/* Service Selection (optional - can be shown above calendar)
          {currentStep === "calendar" &&
            serviceOptions &&
            serviceOptions.length > 0 && (
              <div className="service-selection">
                <h3>
                  <i className="pi pi-tag"></i>
                  Select a Service (Optional)
                </h3>
                <div className="service-grid">
                  {serviceOptions.map((service) => (
                    <div
                      key={service.value}
                      className={`service-card ${
                        selectedService === service.value ? "selected" : ""
                      }`}
                      onClick={() =>
                        setSelectedService(service.value as string)
                      }
                    >
                      <div className="service-name">{service.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )} */}

          {/* Step 1: Calendar */}
          {currentStep === "calendar" && (
            <BookingCalendar
              tenant={tenant}
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
            />
          )}

          {/* Step 2: Time Selection */}
          {currentStep === "time" && selectedDate && (
            <TimeSlotSelection
              tenant={tenant}
              selectedDate={selectedDate}
              selectedService={selectedService}
              selectedProvider=""
              selectedSlot={selectedSlot}
              onSlotSelect={handleSlotSelect}
              onBack={handleBackToCalendar}
              onContinue={handleContinueToDetails}
            />
          )}

          {/* Step 3: Details & Confirmation */}
          {currentStep === "details" && selectedDate && selectedSlot && (
            <FormProvider {...formMethod}>
              <BookingSummary
                tenant={tenant}
                clinicName={clinicName}
                selectedDate={selectedDate}
                selectedSlot={selectedSlot}
                selectedService={selectedService}
                serviceName={serviceName}
                onBack={handleBackToTimeSelection}
                onChangeDate={handleChangeDate}
                onChangeTime={handleChangeTime}
                isSubmitting={isCreating}
                onSubmit={handleSubmit}
                showSuccess={showSuccessModal}
                bookingReference={bookingReference}
                onCloseSuccess={handleCloseSuccess}
                contactEmail={contactInfo.email}
                contactPhone={contactInfo.phone}
                facebookLink={contactInfo.postRegistrationLink}
                hideTime={skipTimeSelection}
              >
                <FormRegistration
                  serviceOptions={serviceOptions}
                  isFetchingServices={isFetchingServices}
                  isEdit={false}
                  selectedDate={selectedDate}
                  selectedTime={selectedSlot?.start_time}
                  hideTimeField={skipTimeSelection}
                  showServicesInPublicBooking={showServicesFromSchedule}
                />
              </BookingSummary>
            </FormProvider>
          )}
        </div>
      </div>

      <Dialog
        visible={showBookingLimitModal}
        onHide={() => setShowBookingLimitModal(false)}
        header="Booking Limit Reached"
        modal
        closable
        style={{ width: "30rem" }}
        footer={
          <Button
            label="OK"
            onClick={() => setShowBookingLimitModal(false)}
            autoFocus
          />
        }
      >
        <p>{bookingLimitMessage}</p>
      </Dialog>
    </div>
  );
}

export default PublicBookingFlow;
